/**
 * Common validation utility for form error handling
 * Provides functions to set errors, focus on first error input, and display error messages
 */

/**
 * Sets validation errors and focuses on the first input with an error
 * @param {Object} errors - Object with field names as keys and error messages as values
 * @param {React.RefObject} inputRefs - Object with field names as keys and ref objects as values
 * @param {Function} setErrors - State setter function for errors
 * @returns {boolean} - Returns true if there are errors, false otherwise
 */
export const handleValidationErrors = (errors, inputRefs = {}, setErrors) => {
  if (!errors || Object.keys(errors).length === 0) {
    return false;
  }

  // Set the errors in state
  if (setErrors) {
    setErrors(errors);
  }

  // Focus on the first input with an error
  const errorKeys = Object.keys(errors);
  if (errorKeys.length > 0) {
    const firstErrorField = errorKeys[0];
    const inputRef = inputRefs[firstErrorField];
    
    // Use setTimeout to ensure the error state is set before focusing
    setTimeout(() => {
      if (inputRef && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }

  return true;
};

/**
 * Validates a single field value
 * @param {string} fieldName - Name of the field
 * @param {any} value - Value to validate
 * @param {Object} rules - Validation rules object
 * @returns {string} - Error message if validation fails, empty string if valid
 */
export const validateField = (fieldName, value, rules = {}) => {
  const {
    required = false,
    minLength = null,
    maxLength = null,
    pattern = null,
    custom = null,
    message = {}
  } = rules;

  // Required validation
  if (required && (!value || value.toString().trim() === '')) {
    return message.required || `${fieldName} is required`;
  }

  // Skip other validations if value is empty and not required
  if (!value || value.toString().trim() === '') {
    return '';
  }

  // Min length validation
  if (minLength && value.toString().length < minLength) {
    return message.minLength || `${fieldName} must be at least ${minLength} characters`;
  }

  // Max length validation
  if (maxLength && value.toString().length > maxLength) {
    return message.maxLength || `${fieldName} must not exceed ${maxLength} characters`;
  }

  // Pattern validation
  if (pattern && !pattern.test(value)) {
    return message.pattern || `${fieldName} format is invalid`;
  }

  // Number minimum validation
  if (rules.type === 'number' && rules.min !== undefined) {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < rules.min) {
      return message.min || `${fieldName} must be at least ${rules.min}`;
    }
  }

  // Custom validation
  if (custom && typeof custom === 'function') {
    const customError = custom(value);
    if (customError) {
      return customError;
    }
  }

  return '';
};

/**
 * Validates multiple fields at once
 * @param {Object} formData - Object with field names as keys and values
 * @param {Object} validationRules - Object with field names as keys and rule objects as values
 * @returns {Object} - Object with field names as keys and error messages as values
 */
export const validateForm = (formData, validationRules = {}) => {
  const errors = {};

  Object.keys(validationRules).forEach((fieldName) => {
    const value = formData[fieldName];
    const rules = validationRules[fieldName];
    
    // Handle custom validation that needs access to other form data
    if (rules.custom && typeof rules.custom === 'function') {
      const customError = rules.custom(value, formData);
      if (customError) {
        errors[fieldName] = customError;
        return; // Skip other validations if custom validation fails
      }
    }
    
    // Create a modified rules object without custom for validateField
    const { custom, ...rulesForField } = rules;
    const error = validateField(fieldName, value, rulesForField);

    if (error) {
      errors[fieldName] = error;
    }
  });

  return errors;
};

/**
 * Clears error for a specific field
 * @param {string} fieldName - Name of the field
 * @param {Function} setErrors - State setter function for errors
 */
export const clearFieldError = (fieldName, setErrors) => {
  if (setErrors) {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }
};

/**
 * Validates password strength
 * Password must be at least 6 characters and contain:
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 * @param {string} password - Password to validate
 * @returns {string} - Error message if validation fails, empty string if valid
 */
export const validatePassword = (password) => {
  if (!password) {
    return 'Password is required';
  }

  if (password.length < 6) {
    return 'Password must be at least 6 characters';
  }

  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

  if (!hasUpperCase) {
    return 'Password must contain at least one uppercase letter';
  }

  if (!hasLowerCase) {
    return 'Password must contain at least one lowercase letter';
  }

  if (!hasNumber) {
    return 'Password must contain at least one number';
  }

  if (!hasSpecialChar) {
    return 'Password must contain at least one special character';
  }

  return '';
};
